import React, { useState, useRef, useEffect } from 'react';
import { CircularProgress } from '@mui/material';
import styles from './SortControls.module.css';

const SortControls = ({ onSort, isLoading, sortBy, setSortBy, sortOrder, setSortOrder }) => {
  const [isNameDropdownOpen, setIsNameDropdownOpen] = useState(false);
  const [nameSortOrder, setNameSortOrder] = useState(
    sortBy === 'name' ? sortOrder : 'ASC'
  );
  const nameDropdownRef = useRef(null);

  const [isDateDropdownOpen, setIsDateDropdownOpen] = useState(false);
  const [dateSortOrder, setDateSortOrder] = useState(
    sortBy === 'date' ? sortOrder : 'DESC'
  );
  const dateDropdownRef = useRef(null);

  useEffect(() => {
    if (sortBy === 'name') {
      setNameSortOrder(sortOrder);
    }
    if (sortBy === 'date') {
      setDateSortOrder(sortOrder);
    }
  }, [sortBy, sortOrder]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (nameDropdownRef.current && !nameDropdownRef.current.contains(event.target)) {
        setIsNameDropdownOpen(false);
      }
      if (dateDropdownRef.current && !dateDropdownRef.current.contains(event.target)) {
        setIsDateDropdownOpen(false);
      }
    };
    if (isNameDropdownOpen || isDateDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isNameDropdownOpen, isDateDropdownOpen]);

  const handleNameButtonClick = () => {
    if (isLoading) return;
    setIsDateDropdownOpen(false);
    setIsNameDropdownOpen((prev) => !prev);
  };

  const handleNameOptionSelect = (order) => {
    setNameSortOrder(order);
    setSortBy('name');
    setSortOrder(order);
    onSort('name', order);
    setIsNameDropdownOpen(false);
  };

  const handleDateButtonClick = () => {
    if (isLoading) return;
    setIsNameDropdownOpen(false);
    setIsDateDropdownOpen((prev) => !prev);
  };

  const handleDateOptionSelect = (order) => {
    setDateSortOrder(order);
    setSortBy('date');
    setSortOrder(order);
    onSort('date', order);
    setIsDateDropdownOpen(false);
  };

  const handleSortByChange = (newSortBy) => {
    if (newSortBy === 'layout') {
      setSortBy('layout');
      setSortOrder('DESC');
      onSort('layout', 'DESC');
      return;
    }

    const newOrder = sortBy === newSortBy
      ? (sortOrder === 'ASC' ? 'DESC' : 'ASC')
      : 'ASC';

    setSortBy(newSortBy);
    setSortOrder(newOrder);
    onSort(newSortBy, newOrder);
  };

  const handleClearSort = () => {
    setSortBy('layout');
    setSortOrder('DESC');
    onSort('layout', 'DESC');
    setIsNameDropdownOpen(false);
    setIsDateDropdownOpen(false);
  };

  return (
    <div className={styles.sortContainer}>
      <div className={styles.sortWrapper}>
        {/* Sort By Section */}
        <div className={styles.sortSection}>
          <span className={styles.sortLabel}>Sort by:</span>
          <div className={styles.sortButtons}>
            <div className={styles.nameDropdownWrapper} ref={nameDropdownRef}>
              <button
                className={`${styles.sortButton} ${sortBy === 'name' ? styles.active : ''}`}
                onClick={handleNameButtonClick}
                disabled={isLoading}
              >
                {isLoading && sortBy === 'name' ? (
                  <CircularProgress size={16} className={styles.buttonLoader} />
                ) : (
                  <svg
                    className={styles.buttonIcon}
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={sortBy === 'name' ? 2.5 : 2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    {sortBy === 'name' ? (
                      <>
                        <path d="M2 6h9" />
                        <path d="M2 12h6" />
                        <path d="M2 18h3" />
                        <path d="M18 4v16" />
                        {sortOrder === 'ASC' ? (
                          <path d="M14 16l4 4 4-4" />
                        ) : (
                          <path d="M14 8l4-4 4 4" />
                        )}
                      </>
                    ) : (
                      <path d="M4 6h16M4 12h10M4 18h6" />
                    )}
                  </svg>
                )}
                {sortBy === 'name' && sortOrder === 'DESC' ? 'Z-A' : 'A-Z'}
                <svg
                  className={`${styles.caretIcon} ${isNameDropdownOpen ? styles.caretOpen : ''}`}
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </button>
              {isNameDropdownOpen && (
                <div className={styles.nameDropdownMenu}>
                  <button
                    type="button"
                    className={`${styles.nameDropdownOption} ${sortBy === 'name' && nameSortOrder === 'ASC' ? styles.nameDropdownOptionSelected : ''}`}
                    onClick={() => handleNameOptionSelect('ASC')}
                  >
                    <span>A - Z</span>
                    {sortBy === 'name' && nameSortOrder === 'ASC' && (
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M20 6L9 17l-5-5" />
                      </svg>
                    )}
                  </button>
                  <button
                    type="button"
                    className={`${styles.nameDropdownOption} ${sortBy === 'name' && nameSortOrder === 'DESC' ? styles.nameDropdownOptionSelected : ''}`}
                    onClick={() => handleNameOptionSelect('DESC')}
                  >
                    <span>Z - A</span>
                    {sortBy === 'name' && nameSortOrder === 'DESC' && (
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M20 6L9 17l-5-5" />
                      </svg>
                    )}
                  </button>
                </div>
              )}
            </div>
            <div className={styles.nameDropdownWrapper} ref={dateDropdownRef}>
              <button
                className={`${styles.sortButton} ${sortBy === 'date' ? styles.active : ''}`}
                onClick={handleDateButtonClick}
                disabled={isLoading}
              >
                {isLoading && sortBy === 'date' ? (
                  <CircularProgress size={16} className={styles.buttonLoader} />
                ) : (
                  <svg
                    className={styles.buttonIcon}
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                    <path d="M16 2v4M8 2v4M3 10h18" />
                  </svg>
                )}
                {sortBy === 'date'
                  ? (sortOrder === 'ASC' ? 'Oldest First' : 'Newest First')
                  : 'Date Uploaded'}
                <svg
                  className={`${styles.caretIcon} ${isDateDropdownOpen ? styles.caretOpen : ''}`}
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </button>
              {isDateDropdownOpen && (
                <div className={styles.nameDropdownMenu}>
                  <button
                    type="button"
                    className={`${styles.nameDropdownOption} ${sortBy === 'date' && dateSortOrder === 'DESC' ? styles.nameDropdownOptionSelected : ''}`}
                    onClick={() => handleDateOptionSelect('DESC')}
                  >
                    <span>Newest First</span>
                    {sortBy === 'date' && dateSortOrder === 'DESC' && (
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M20 6L9 17l-5-5" />
                      </svg>
                    )}
                  </button>
                  <button
                    type="button"
                    className={`${styles.nameDropdownOption} ${sortBy === 'date' && dateSortOrder === 'ASC' ? styles.nameDropdownOptionSelected : ''}`}
                    onClick={() => handleDateOptionSelect('ASC')}
                  >
                    <span>Oldest First</span>
                    {sortBy === 'date' && dateSortOrder === 'ASC' && (
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M20 6L9 17l-5-5" />
                      </svg>
                    )}
                  </button>
                </div>
              )}
            </div>
            <button
              className={`${styles.sortButton} ${sortBy === 'layout' ? styles.active : ''}`}
              onClick={() => handleSortByChange('layout')}
              disabled={isLoading}
            >
              {isLoading && sortBy === 'layout' ? (
                <CircularProgress size={16} className={styles.buttonLoader} />
              ) : (
                <svg
                  className={styles.buttonIcon}
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <rect x="3" y="3" width="7" height="7" />
                  <rect x="14" y="3" width="7" height="7" />
                  <rect x="3" y="14" width="7" height="7" />
                  <rect x="14" y="14" width="7" height="7" />
                </svg>
              )}
              Layout
            </button>
          </div>
        </div>

        {/* Clear Sort Button - Only show if not default (layout DESC) */}
        {(sortBy !== 'layout' || sortOrder !== 'DESC') && (
          <div className={styles.clearSection}>
            <button
              className={styles.clearButton}
              onClick={handleClearSort}
              disabled={isLoading}
              title="Reset to default (Layout)"
            >
              <svg
                className={styles.clearIcon}
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
              <span>Reset</span>
            </button>
          </div>
        )}
      </div>

      {/* Loading Overlay */}

    </div>
  );
};

export default SortControls;
